import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";
import { Search, FolderLock, Download, Trash2, Image as ImageIcon, Film, Mic, FileText } from "lucide-react";

const FOLDERS = ["All", "Vehicle Damage", "Other Vehicle", "Other Driver's Info", "Other Driver's Insurance Card", "Other Driver's License", "Roadway", "Injuries", "Witnesses", "Police"];
const MEDIA_ICON = { image: ImageIcon, video: Film, audio: Mic, file: FileText };

export default function EvidenceVault() {
  const [items, setItems] = useState([]);
  const [folder, setFolder] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const token = getToken();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (folder !== "All") params.folder = folder;
      if (q) params.q = q;
      const { data } = await api.get("/evidence", { params });
      setItems(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [folder]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const remove = async (id) => {
    try { await api.delete(`/evidence/${id}`); setItems((p) => p.filter((e) => e.evidence_id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const downloadUrl = (ev) => `${process.env.REACT_APP_BACKEND_URL}/api/evidence/${ev.evidence_id}/download?auth=${token}`;

  return (
    <AppLayout>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Secure Storage</p>
      <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Evidence Vault</h1>
      <p className="mt-2 text-slate-600">All your evidence, organized and secured in one place.</p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vault-search" placeholder="Search files…" className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FOLDERS.map((f) => (
          <button key={f} onClick={() => setFolder(f)} data-testid={`vault-folder-${f}`} className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${folder === f ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{f}</button>
        ))}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-12 text-center">
            <FolderLock className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No evidence here yet. Add files from your accident report.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((ev) => {
              const MI = MEDIA_ICON[ev.media_type] || FileText;
              const url = downloadUrl(ev);
              return (
                <div key={ev.evidence_id} className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`vault-item-${ev.evidence_id}`}>
                  <div className="aspect-square bg-slate-50 relative">
                    {ev.media_type === "image" ? <img src={url} alt={ev.original_filename} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><MI className="w-10 h-10" /></div>}
                    <span className="absolute top-2 left-2 bg-navy/80 text-white text-[10px] px-2 py-0.5 rounded-full">{ev.folder}</span>
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-medium text-navy truncate">{ev.original_filename}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{new Date(ev.created_at).toLocaleDateString()}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <a href={url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-cobalt bg-cobalt-light rounded-full py-1.5 hover:bg-cobalt hover:text-white transition-all"><Download className="w-3 h-3" /> Open</a>
                      <button onClick={() => remove(ev.evidence_id)} className="p-1.5 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
